// PRD.319 — Dental Investigations.
//
// Encounter documentation of a diagnostic investigation:
//
//     type + optional tooth context + findings
//
// NO INFERENCE, IN EITHER DIRECTION
// --------------------------------
// Chart context may preselect a TOOTH; it must never choose the investigation
// (no caries→IOPA, impacted→CBCT, missing→OPG — PRD §14). And findings never
// create or modify a Diagnosis or a Treatment (PRD §15, §16). This module
// contains no mapping of any kind between clinical concepts.
//
// Tooth associations reuse `dentalToothLinkId` / `groupToothLinks` / `diffTeeth`
// from `dental-treatment-logic`, so investigations, treatments and their
// records all share one tooth-link implementation.
//
// Self-contained (no workspace-package imports) so it runs under node:test.

// ─── Investigation types ───

/**
 * Common dental investigations, offered as a typeahead.
 *
 * A UI CONVENIENCE ONLY — the column is free text with no database enum
 * (PRD §6), so a clinic can record a term this list has never heard of and
 * nothing rejects it. That is also how "Other" works: whatever the clinician
 * names it becomes the stored type, and `findings` is never overloaded with the
 * investigation's name.
 */
export const COMMON_INVESTIGATION_TYPES = [
  'IOPA',
  'RVG',
  'OPG',
  'CBCT',
  'Lateral Cephalogram',
] as const;

/** Filter the common types for the quick-select. Never restricts what may be saved. */
export function searchInvestigationTypes(
  query: string,
  types: readonly string[] = COMMON_INVESTIGATION_TYPES,
): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...types];
  return types.filter((t) => t.toLowerCase().includes(q));
}

/**
 * Normalize a typed investigation type.
 *
 * Trims only. Deliberately does NOT title-case, expand abbreviations or map to
 * a canonical form: "opg" and "OPG" are the clinician's wording to choose, and
 * silently rewriting a clinical field is worse than an inconsistent list.
 */
export function normalizeInvestigationType(value: string): string {
  return value.trim();
}

// ─── Rows ───

export interface DentalInvestigationRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  type: string | null;
  findings: string | null;
  created_by_user_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

// ─── Selection ───

/**
 * This encounter's investigations, oldest first.
 *
 * Encounter-scoped by design: a prior visit's radiograph interpretation is its
 * own record and must never be rewritten by today's edits (PRD §5).
 */
export function selectEncounterInvestigations(
  rows: readonly DentalInvestigationRow[],
  encounterId: string,
): DentalInvestigationRow[] {
  return rows
    .filter((r) => r.encounter_id === encounterId)
    .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
}

/** Display type, falling back to a neutral placeholder rather than inventing one. */
export function resolveInvestigationType(row: { type: string | null }): string {
  return (row.type ?? '').trim();
}
